import { Button, toast } from "@relex-solutions/fragments"
import { useCallback, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"

interface QueryErrorToastProps {
  /**
   * The error from React Query
   */
  error: Error | null
  /**
   * Whether the query is in an error state
   */
  isError: boolean
  /**
   * Function to refetch the query
   */
  refetch?: () => void
  /**
   * Auto dismiss timeout in milliseconds (default: 0ms = no auto dismiss)
   */
  autoDismissMs?: number
  /**
   * Custom error message to display as title
   */
  customMessage?: string
}

const TOAST_ID = "query-error-toast"

const getHttpErrorDetails = (error: Error): string => {
  const httpError = error as Error & {
    status?: number
    statusText?: string
  }

  if (httpError.status || httpError.statusText) {
    const statusPart = httpError.status ? `Status: ${httpError.status.toString()}` : ""
    const statusTextPart = httpError.statusText ? ` - ${httpError.statusText}` : ""
    return `${statusPart}${statusTextPart}`
  }

  return error.message
}

export function QueryErrorToast({ error, isError, refetch, autoDismissMs = 0, customMessage }: QueryErrorToastProps) {
  const { t } = useTranslation()
  const lastShownErrorRef = useRef<Error | null>(null)

  const createBodyContent = useCallback(
    (httpErrorDetails: string) => (
      <div>
        <div style={{ marginTop: "var(--fr-size-8)" }}>{httpErrorDetails}</div>
        {refetch && (
          <Button
            variant="secondary"
            size="small"
            style={{ marginTop: "var(--fr-size-14)" }}
            onClick={() => {
              refetch()
              lastShownErrorRef.current = null
              toast.dismiss(TOAST_ID)
            }}
          >
            {t("visualization.error.retry")}
          </Button>
        )}
      </div>
    ),
    [refetch, t]
  )

  const showErrorToast = useCallback(
    (error: Error) => {
      const errorTitle = customMessage ?? t("visualization.error.unknown")
      const httpErrorDetails = getHttpErrorDetails(error)
      const bodyContent = createBodyContent(httpErrorDetails)

      toast.error(
        {
          title: errorTitle,
          body: bodyContent
        },
        {
          id: TOAST_ID,
          position: "bottom-center",
          ...(autoDismissMs > 0 && { duration: autoDismissMs })
        }
      )

      lastShownErrorRef.current = error
    },
    [customMessage, t, createBodyContent, autoDismissMs]
  )

  useEffect(() => {
    if (isError && error && lastShownErrorRef.current !== error) {
      showErrorToast(error)
    } else if (!isError) {
      lastShownErrorRef.current = null
    }
  }, [isError, error, showErrorToast])

  return null
}
